import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PACKAGE_OPTIONS } from "../data/content";
import DatePicker from "./DatePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const INITIAL = {
  full_names: "",
  email: "",
  phone: "",
  event_date: "",
  location: "",
  package_interest: "",
  guest_count: "",
  vision_story: "",
};

const inputCls =
  "w-full bg-transparent border-b border-white/15 focus:border-champagne outline-none py-3 text-sm font-light text-ivory placeholder:text-stone-600 transition-colors duration-300";

export default function ContactForm() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const onSelect = (e) => setForm((f) => ({ ...f, package_interest: e.detail }));
    window.addEventListener("select-package", onSelect);
    return () => window.removeEventListener("select-package", onSelect);
  }, []);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.event_date) {
      toast.error("Please choose your wedding date.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        phone: form.phone || null,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
      });
      setSent(true);
      setForm(INITIAL);
      toast.success("Your commission enquiry has been received.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong while sending. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-enquiry-section" className="relative py-28 md:py-40 bg-[#0D0D10]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-12 md:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          className="md:col-span-5"
        >
          <p className="text-[11px] font-mono tracking-[0.25em] uppercase text-stone-500">
            07 // Inquire
          </p>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-ivory leading-tight">
            Begin Your <span className="italic text-champagne">Commission</span>
          </h2>
          <p className="mt-6 max-w-md text-sm sm:text-base font-light leading-relaxed text-stone-400">
            Share the outline of your day — where, when, and how you imagine it unfolding. We accept
            a limited number of weddings each year and reply to every enquiry within 48 hours.
          </p>
          <div className="mt-10 space-y-4 text-sm font-light text-stone-300">
            <p>
              <span className="block text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">Studio</span>
              studio@elegancenoire.com
            </p>
            <p>
              <span className="block text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">Based Between</span>
              Paris · Lake Como · Worldwide
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.12, ease: EASE }}
          className="md:col-span-7"
        >
          {sent ? (
            <div
              data-testid="enquiry-success-message"
              className="border border-gold/40 bg-elevated p-10 text-center"
            >
              <p className="font-serif text-2xl text-champagne italic">Merci — it is on its way.</p>
              <p className="mt-4 text-sm font-light text-stone-300 leading-relaxed">
                Your commission enquiry has been received. Julian or Clara will write back
                personally within 48 hours.
              </p>
              <button
                data-testid="enquiry-another-button"
                onClick={() => setSent(false)}
                className="mt-8 border border-champagne/40 px-6 py-3 text-[11px] font-mono tracking-[0.2em] uppercase text-champagne hover:bg-champagne hover:text-obsidian transition-colors duration-300"
              >
                Send Another Enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="enquiry-form">
              <div>
                <label htmlFor="enquiry-names" className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">
                  Full Names / Couple Names *
                </label>
                <input id="enquiry-names" data-testid="enquiry-names-input" required value={form.full_names} onChange={update("full_names")} className={inputCls} placeholder="Ava & Noah Bennett" />
              </div>
              <div>
                <label htmlFor="enquiry-email" className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">
                  Email Address *
                </label>
                <input id="enquiry-email" type="email" data-testid="enquiry-email-input" required value={form.email} onChange={update("email")} className={inputCls} placeholder="you@example.com" />
              </div>
              <div>
                <label htmlFor="enquiry-phone" className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">
                  Phone / WhatsApp
                </label>
                <input id="enquiry-phone" type="tel" data-testid="enquiry-phone-input" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="+33 6 00 00 00 00" />
              </div>
              <div>
                <label htmlFor="enquiry-date" className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">
                  Target Wedding Date *
                </label>
                <DatePicker value={form.event_date} onChange={(v) => setForm((f) => ({ ...f, event_date: v }))} />
              </div>
              <div>
                <label htmlFor="enquiry-location" className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">
                  Venue & Destination *
                </label>
                <input id="enquiry-location" data-testid="enquiry-location-input" required value={form.location} onChange={update("location")} className={inputCls} placeholder="Villa Balbiano, Lake Como" />
              </div>
              <div>
                <label htmlFor="enquiry-package" className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">
                  Collection of Interest *
                </label>
                <select id="enquiry-package" data-testid="enquiry-package-select" required value={form.package_interest} onChange={update("package_interest")} className={`${inputCls} bg-transparent`}>
                  <option value="" disabled className="bg-surface">Select a collection</option>
                  {PACKAGE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt} className="bg-surface">{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="enquiry-guests" className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">
                  Estimated Guest Count
                </label>
                <input id="enquiry-guests" type="number" min="1" data-testid="enquiry-guests-input" value={form.guest_count} onChange={update("guest_count")} className={inputCls} placeholder="120" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="enquiry-vision" className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">
                  Tell Us About Your Vision & Story *
                </label>
                <textarea id="enquiry-vision" data-testid="enquiry-vision-input" required rows={4} value={form.vision_story} onChange={update("vision_story")} className={`${inputCls} resize-none`} placeholder="How you met, the feeling you want the day to hold, the light you dream of..." />
              </div>
              <div className="sm:col-span-2 pt-2">
                <button
                  type="submit"
                  data-testid="enquiry-submit-button"
                  disabled={sending}
                  className="w-full sm:w-auto bg-champagne text-obsidian px-10 py-4 text-[11px] font-mono tracking-[0.2em] uppercase hover:bg-ivory transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Transmitting..." : "Transmit Wedding Commission"}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
